import { vaultTelemetryBlocked } from '@/lib/vault-telemetry';

const TRACKING_HOST = 'plfalg.kernel.sh';
const PIXEL_VERSION = '1.1.0';
const HANDOFF_PARAM = 'ca_device_id';
const REDIRECT_PARAM = 'redirect_url';
const MAX_REDIRECT_DEPTH = 3;

type ConceptualQueue = {
  (...args: unknown[]): void;
  q?: unknown[][];
  getDeviceId?: () => string;
};

declare global {
  interface Window {
    ca?: ConceptualQueue;
    __CA_CONFIG?: Record<string, unknown>;
  }
}

export const CONCEPTUAL_CONSENT_CATEGORY = 'marketing' as const;

/**
 * Honouring Do Not Track has to cover the whole integration, not just the
 * vendor's events. respectDNT stops the pixel sending, but it doesn't stop it
 * minting or adopting a device ID — so the pixel is never loaded at all when
 * the header is set.
 */
function doNotTrackEnabled(): boolean {
  if (typeof navigator === 'undefined') return false;

  const legacy = navigator as Navigator & { msDoNotTrack?: string };
  return (
    navigator.doNotTrack === '1' ||
    legacy.msDoNotTrack === '1' ||
    (window as Window & { doNotTrack?: string }).doNotTrack === '1'
  );
}

// The pixel has no shutdown API and can't be unloaded once injected, so a
// withdrawal is enforced here rather than by the vendor: every path that could
// send an event checks this first.
let stopped = false;

export function conceptualLoaderSrc(pixelKey: string): string {
  return `https://${TRACKING_HOST}/analytics/loader-v1.js?key=${encodeURIComponent(pixelKey)}&v=${PIXEL_VERSION}`;
}

function parseUrl(href: string, base?: string): URL | null {
  try {
    return new URL(href, base);
  } catch {
    return null;
  }
}

/**
 * The marketing site appends its device ID to links pointing here. Signed-out
 * visitors get bounced through sign-in on the way, which nests the original URL
 * — and the parameter with it — inside redirect_url, so it isn't always a
 * top-level parameter by the time a page renders.
 */
export function readHandedOffDeviceId(href: string): string | null {
  let current = parseUrl(href);

  for (let depth = 0; current && depth < MAX_REDIRECT_DEPTH; depth += 1) {
    const deviceId = current.searchParams.get(HANDOFF_PARAM);
    if (deviceId) return deviceId;

    const nested = current.searchParams.get(REDIRECT_PARAM);
    current = nested ? parseUrl(nested, href) : null;
  }

  return null;
}

function adoptHandedOffDeviceId(): void {
  const deviceId = readHandedOffDeviceId(window.location.href);
  if (!deviceId) return;

  // Queued before the loader runs, so the pixel adopts the ID during init and
  // its first page_view is already attributed to the marketing-site visitor.
  window.ca?.('setDeviceId', deviceId);

  const url = new URL(window.location.href);
  if (url.searchParams.has(HANDOFF_PARAM)) {
    url.searchParams.delete(HANDOFF_PARAM);
    // Next keeps its router data in history state; replacing it with {} strips
    // that and breaks back-navigation, so the existing state is carried over.
    history.replaceState(history.state, '', url);
  }
}

export function loadConceptualPixel(pixelKey: string): void {
  if (doNotTrackEnabled() || vaultTelemetryBlocked()) return;

  // Re-granting after a withdrawal reaches here with the script already on the
  // page, so only a first load injects it.
  if (window.ca) {
    stopped = false;
    return;
  }

  stopped = false;

  // measure-v1.js reads __CA_CONFIG once at init, so it has to be set before
  // the loader injects it. Both flags are off in the shipped pixel config.
  //
  // trackPageViews and trackUnload are what make a withdrawal enforceable.
  // Left on, the vendor emits events we can't gate: a page view at init — which
  // fires even if consent is withdrawn while the loader is still in flight — and
  // scroll, click and form-submit listeners, all routed through its own internal
  // track() rather than through ours. Off, the script emits nothing on its own
  // and every event is one we sent.
  //
  // respectDNT takes effect: the pixel checks it before every event.
  // anonymizeIP currently does not — the shipped script defines it and never
  // reads it, and resolves the visitor's full address from the vendor's IP
  // endpoint to send as client_ip regardless. It's set here so it applies if
  // the vendor implements it; until then full-IP processing is what happens.
  window.__CA_CONFIG = {
    ...window.__CA_CONFIG,
    respectDNT: true,
    anonymizeIP: true,
    trackPageViews: false,
    trackUnload: false,
  };

  const queue: ConceptualQueue = (...args: unknown[]) => {
    (queue.q = queue.q ?? []).push(args);
  };
  window.ca = queue;

  adoptHandedOffDeviceId();

  const script = document.createElement('script');
  script.async = true;
  script.src = conceptualLoaderSrc(pixelKey);
  document.head.appendChild(script);
}

// The pixel only defines ca.track/ca.identify once measure-v1.js has loaded;
// the ca('track', ...) form is queued before that and dispatched after.
export function conceptualTrack(
  event: string,
  properties?: Record<string, unknown>,
  options?: { dedupeId: string },
): void {
  if (stopped || vaultTelemetryBlocked()) return;
  window.ca?.('track', event, properties, options);
}

export function conceptualIdentify(userId: string, traits?: Record<string, unknown>): void {
  if (stopped || vaultTelemetryBlocked()) return;
  window.ca?.('identify', userId, traits);
}

/**
 * Stops the pixel after consent is withdrawn. deidentify() clears the stored
 * identity — user, device ID, click-ID cookies — but rotates rather than halts,
 * so everything that could send an event is gated behind `stopped` as well.
 */
export function revokeConceptualPixel(): void {
  if (!window.ca) return;

  stopped = true;

  // Anything queued before the script finished loading would otherwise dispatch
  // when it does, which is the window a withdrawal mid-load falls into.
  if (window.ca) window.ca.q = [];

  window.ca?.('deidentify');
}

export function isConceptualStopped(): boolean {
  return stopped;
}
